<script lang="ts">
    import Vue from "vue";
    import { Component } from "vue-facing-decorator";
    import { ProjectPlanPickerDialogViewModel } from "@Quest/PortfolioScenarios/Components/ProjectPlanPickerDialog";

    type PlanItem = { Id: string; Name: string; CreatedAt?: string };

    @Component({ props: { viewModel: { type: Object, required: true } } })
    export default class ProjectPlanPickerDialog extends Vue
    {
        get vm(): ProjectPlanPickerDialogViewModel
        {
            return this.$props.viewModel as ProjectPlanPickerDialogViewModel;
        }

        get opened(): boolean { return !!this.vm?.opened; }
        get initialized(): boolean { return !!this.vm.initialized; }

        get projectName(): string { return this.vm.projectName || "Project"; }
        get plans(): PlanItem[] { return (this.vm.plans as unknown) as PlanItem[]; }
        get activePlanId(): string | null { return this.vm.activePlanId; }

        get lockedPlanId(): string | null { return this.vm.lockedPlanId; }
        set lockedPlanId(val: string | null) { this.vm.lockedPlanId = val; }

        get canConfirm(): boolean { return this.initialized; }

        get orderedPlans(): PlanItem[]
        {
            const arr = [...this.plans];
            const active = this.activePlanId ? arr.find(p => p.Id === this.activePlanId) : undefined;
            const rest = arr.filter(p => p.Id !== this.activePlanId);

            rest.sort((a, b) =>
            {
                const ad = a.CreatedDate ? Date.parse(a.CreatedDate) : NaN;
                const bd = b.CreatedDate ? Date.parse(b.CreatedDate) : NaN;
                if (!Number.isNaN(ad) && !Number.isNaN(bd)) return bd - ad;
                if (!Number.isNaN(ad)) return -1;
                if (!Number.isNaN(bd)) return 1;
                return a.Name.localeCompare(b.Name);
            });

            return active ? [active, ...rest] : rest;
        }

        toggleLock(id: string)
        {
            this.lockedPlanId = (this.lockedPlanId === id) ? null : id;
        }

        resetToDefault()
        {
            this.lockedPlanId = null;
        }

        handleConfirm()
        {
            this.$emit("confirmed", {
                projectId: this.vm.projectId,
                lockedPlanId: this.lockedPlanId
            });
        }

        close() { this.vm.close(); }
    }
</script>

<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-dialog-scrollable modal-lg">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Lock a plan version: {{ projectName }}</h5>
                        <button @click="close" type="button" class="close" aria-label="Close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <!-- Loading -->
                        <div v-if="!initialized" class="text-center my-5">
                            <h5>
                                <i class="fa fa-spinner fa-spin mr-2"></i>
                                Loading plans...
                            </h5>
                        </div>

                        <!-- Loaded -->
                        <div v-else>
                            <div class="alert alert-info mb-3" role="alert">
                                By default, this scenario always follows the project’s current <em>Active</em> plan.
                                You can lock it to a specific version below if needed.
                                <button v-if="lockedPlanId !== null"
                                        type="button"
                                        class="btn btn-link p-0 align-baseline"
                                        @click="resetToDefault">
                                    Reset to default
                                </button>
                            </div>

                            <div class="list-group">
                                <button v-for="p in orderedPlans"
                                        :key="p.Id"
                                        type="button"
                                        class="list-group-item list-group-item-action d-flex justify-content-between align-items-center plan-row"
                                        @click="toggleLock(p.Id)"
                                        :aria-pressed="lockedPlanId === p.Id"
                                        :title="p.Name">
                                    <span class="d-flex align-items-center">
                                        <i class="mr-2"
                                           :class="lockedPlanId === p.Id ? 'fa-solid fa-circle-check text-primary' : 'fa-regular fa-circle text-muted'"
                                           aria-hidden="true"></i>
                                        <span :class="{ 'font-weight-semibold': lockedPlanId === p.Id }">
                                            {{ p.Name }}
                                        </span>
                                    </span>
                                    <span v-if="p.Id === activePlanId" class="badge badge-success">Active</span>
                                </button>

                                <div v-if="orderedPlans.length === 0" class="list-group-item text-muted small">
                                    No plans found for this project.
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button @click="close" type="button" class="btn btn-outline-secondary">Cancel</button>
                        <button @click="handleConfirm" type="button" class="btn btn-primary" :disabled="!canConfirm">
                            Confirm
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
    .font-weight-semibold {
        font-weight: 600;
    }

    .plan-row {
        padding-top: .6rem;
        padding-bottom: .6rem;
    }
</style>
